/**
 * Centralized API Service
 *
 * Provides a singleton API client instance with automatic
 * auth token injection, response/error handling, and 401
 * refresh-token interceptor.
 */
import type { AxiosInstance } from 'axios';
import { createApiClient } from '@tms-phoenix/ui-vue';
import { config } from '@/core/config';

let apiInstance: AxiosInstance | null = null;

/**
 * Get the singleton API client instance
 * Automatically includes auth token from auth store when available
 */
export function getApiClient(): AxiosInstance {
  if (!apiInstance) {
    apiInstance = createApiClient({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      getToken: () => {
        // Dynamically get token from localStorage to avoid circular dependency
        // This will be automatically injected into requests
        try {
          const raw = localStorage.getItem('pinia-auth');
          if (!raw) return null;
          return JSON.parse(raw)?.accessToken ?? null;
        } catch {
          return null;
        }
      },
      // ── 401 token-refresh callback ─────────────────────────────────────
      // Called by createApiClient when any authenticated request returns 401.
      // Lazy-imports auth store to break the circular dependency
      // (auth.store → api.service → auth.store).
      // The store's refreshAccessToken() handles clearAuth() + /login redirect
      // on failure, so we only need to return the new token or null here.
      onRefreshToken: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        const authStore = useAuthStore();
        const success = await authStore.refreshAccessToken();
        return success ? authStore.accessToken : null;
      },
      skipRefreshForUrls: ['/auth/login', '/auth/logout', '/auth/refresh'],
      // A refreshed token that still 401s means the session is invalid server-side —
      // force a client-side logout instead of retrying refresh again.
      onAuthFailure: async () => {
        const { useAuthStore } = await import('@/stores/auth.store');
        useAuthStore().forceLogout();
      },
    });

    // ── Tenant hostname header ───────────────────────────────────────────
    // Send x-tenant-hostname on every request so backend services can
    // resolve the tenant context (Cognito pool, DB schema, etc.).
    apiInstance.interceptors.request.use((requestConfig) => {
      if (requestConfig.headers) {
        requestConfig.headers['x-tenant-hostname'] = window.location.hostname;
      }
      return requestConfig;
    });
  }

  return apiInstance;
}

/**
 * Reset the API client instance (useful for testing or auth changes)
 */
export function resetApiClient(): void {
  apiInstance = null;
}
